import { useEffect, useState, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Loader2, Users, Star, TrendingUp, Building2, Filter, Search } from "lucide-react";
import { fetchTenantSummary, fetchTenantEmployees, fetchTenantBranches } from "../lib/tenantApi";
import TenantShell from "../components/TenantShell";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";

const POTENTIAL_COLOR = {
  yuksek: "text-emerald-700 bg-emerald-50 border-emerald-200",
  orta: "text-amber-700 bg-amber-50 border-amber-200",
  dusuk: "text-slate-600 bg-slate-50 border-slate-200",
};

function KpiCard({ label, value, sub, icon: Icon, color = "#0ea5e9" }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold">{label}</div>
        <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${color}15`, color }}>
          <Icon className="w-4 h-4" />
        </div>
      </div>
      <div className="font-display text-3xl font-bold text-slate-900">{value}</div>
      {sub ? <div className="text-xs text-slate-500 mt-1">{sub}</div> : null}
    </div>
  );
}

function DistributionBar({ label, items, total }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="text-[0.7rem] uppercase tracking-wider text-slate-500 font-semibold mb-3">{label}</div>
      <div className="space-y-2">
        {Object.entries(items)
          .sort(([, a], [, b]) => b - a)
          .slice(0, 8)
          .map(([k, v]) => {
            const pct = Math.round((v / total) * 100);
            return (
              <div key={k} className="text-xs">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-slate-700 truncate">{k}</span>
                  <span className="text-slate-500 ml-2">{v} ({pct}%)</span>
                </div>
                <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                  <div className="h-full bg-sky-500 rounded-full" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
      </div>
    </div>
  );
}

export default function RetailWorkforce() {
  const { slug } = useParams();
  const [summary, setSummary] = useState(null);
  const [employees, setEmployees] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterDept, setFilterDept] = useState("all");
  const [filterRegion, setFilterRegion] = useState("all");
  const [filterTalent, setFilterTalent] = useState(false);
  const [search, setSearch] = useState("");

  useEffect(() => {
    Promise.all([
      fetchTenantSummary(slug),
      fetchTenantEmployees(slug, { limit: 500 }),
      fetchTenantBranches(slug),
    ])
      .then(([s, e, b]) => {
        setSummary(s);
        setEmployees(e.items || []);
        setBranches(b || []);
      })
      .finally(() => setLoading(false));
  }, [slug]);

  const departments = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => e.department && set.add(e.department));
    return [...set].sort();
  }, [employees]);

  const regions = useMemo(() => {
    const set = new Set();
    employees.forEach((e) => e.region && set.add(e.region));
    return [...set].sort();
  }, [employees]);

  const filtered = useMemo(() => {
    return employees.filter((e) => {
      if (filterDept !== "all" && e.department !== filterDept) return false;
      if (filterRegion !== "all" && e.region !== filterRegion) return false;
      if (filterTalent && !e.is_talent) return false;
      if (search && !e.name?.toLowerCase().includes(search.toLowerCase())) return false;
      return true;
    });
  }, [employees, filterDept, filterRegion, filterTalent, search]);

  if (loading) {
    return (
      <TenantShell active="workforce">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
        </div>
      </TenantShell>
    );
  }

  return (
    <TenantShell active="workforce">
      {/* Hero */}
      <div className="mb-8">
        <div className="flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.18em] text-slate-500 font-semibold mb-3">
          <Users className="w-3.5 h-3.5" />
          Çalışan Havuzu
        </div>
        <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900 mb-2">
          Perakende İş Gücü
        </h1>
        <p className="text-slate-600">
          {summary?.headcount_active || 0} aktif çalışan · {summary?.branch_count || 0} şube · {departments.length} departman
        </p>
      </div>

      {/* KPI Cards */}
      {summary && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <KpiCard label="Aktif Çalışan" value={summary.headcount_active} sub={`${summary.headcount_total} toplam`} icon={Users} color="#0ea5e9" />
          <KpiCard label="Yetenek Havuzu" value={summary.talent_pool} sub={`%${Math.round((summary.talent_pool / summary.headcount_active) * 100)}`} icon={Star} color="#10b981" />
          <KpiCard label="Ort. Performans" value={summary.avg_performance?.toFixed(1) || "—"} sub="1-5 ölçek" icon={TrendingUp} color="#f59e0b" />
          <KpiCard label="Şube" value={summary.branch_count} sub={`${summary.managers} yönetici`} icon={Building2} color="#8b5cf6" />
        </div>
      )}

      {/* Distribution */}
      {summary && (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <DistributionBar label="Departman" items={summary.department_distribution} total={summary.headcount_active} />
          <DistributionBar label="Band" items={summary.band_distribution} total={summary.headcount_active} />
          <DistributionBar label="Bölge" items={summary.region_distribution} total={summary.headcount_active} />
        </div>
      )}

      {/* Filters */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="flex items-center gap-2 text-xs text-slate-500 font-medium">
            <Filter className="w-3.5 h-3.5" /> Filtrele:
          </div>
          <div className="relative flex-1 min-w-[200px]">
            <Search className="w-3.5 h-3.5 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              type="text"
              placeholder="İsim ara…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="workforce-search"
              className="w-full pl-9 pr-3 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:border-sky-500"
            />
          </div>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            data-testid="filter-department"
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
          >
            <option value="all">Tüm Departmanlar</option>
            {departments.map((d) => (
              <option key={d} value={d}>{d}</option>
            ))}
          </select>
          <select
            value={filterRegion}
            onChange={(e) => setFilterRegion(e.target.value)}
            data-testid="filter-region"
            className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white"
          >
            <option value="all">Tüm Bölgeler</option>
            {regions.map((r) => (
              <option key={r} value={r}>{r}</option>
            ))}
          </select>
          <label className="flex items-center gap-1.5 text-xs text-slate-700 cursor-pointer">
            <input
              type="checkbox"
              checked={filterTalent}
              onChange={(e) => setFilterTalent(e.target.checked)}
              data-testid="filter-talent"
              className="rounded"
            />
            Yalnız Yetenekler
          </label>
        </div>
      </div>

      {/* Employee table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <div className="text-xs text-slate-500 font-medium">
            <span className="font-bold text-slate-900">{filtered.length}</span> sonuç
          </div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="employee-table">
            <thead className="bg-slate-50 text-xs uppercase tracking-wider text-slate-500 font-semibold">
              <tr>
                <th className="text-left px-4 py-3">Çalışan</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Pozisyon</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Şube / Bölge</th>
                <th className="text-center px-4 py-3">Band</th>
                <th className="text-center px-4 py-3">Perf.</th>
                <th className="text-center px-4 py-3 hidden md:table-cell">Bağlılık</th>
                <th className="text-center px-4 py-3 hidden lg:table-cell">Satış %</th>
                <th className="text-center px-4 py-3">Potansiyel</th>
              </tr>
            </thead>
            <tbody>
              {filtered.slice(0, 100).map((e) => (
                <tr key={e.id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`emp-row-${e.id}`}>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2.5">
                      <Avatar className="w-8 h-8 ring-1 ring-slate-200">
                        <AvatarImage src={e.avatar} alt={e.name} />
                        <AvatarFallback>{e.name?.charAt(0)}</AvatarFallback>
                      </Avatar>
                      <div className="min-w-0">
                        <div className="font-medium text-slate-900 truncate flex items-center gap-1.5">
                          {e.name}
                          {e.is_talent && <Star className="w-3 h-3 text-amber-500 fill-amber-500" />}
                        </div>
                        <div className="text-[0.65rem] text-slate-500">{e.department}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 hidden md:table-cell text-slate-700">{e.title}</td>
                  <td className="px-4 py-3 hidden lg:table-cell text-slate-600 text-xs">
                    <div className="truncate max-w-[180px]">{e.branch_name || "—"}</div>
                    <div className="text-slate-400">{e.region}</div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="inline-block px-2 py-0.5 rounded font-mono text-xs bg-slate-100 text-slate-700">{e.salary_band}</span>
                  </td>
                  <td className="px-4 py-3 text-center font-mono text-xs">
                    {e.performance_rating ? (
                      <span className="text-slate-800">
                        {e.performance_rating.toFixed(1)} <span className="text-slate-400">{e.performance_trend}</span>
                      </span>
                    ) : (
                      "—"
                    )}
                  </td>
                  <td className="px-4 py-3 text-center hidden md:table-cell text-xs">
                    {e.engagement_score ? (
                      <span className={`font-semibold ${e.engagement_score >= 70 ? "text-emerald-600" : e.engagement_score >= 50 ? "text-amber-600" : "text-rose-600"}`}>
                        %{e.engagement_score}
                      </span>
                    ) : "—"}
                  </td>
                  <td className="px-4 py-3 text-center hidden lg:table-cell text-xs">
                    {e.avg_sales_achievement_pct ? `%${e.avg_sales_achievement_pct}` : "—"}
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className={`inline-block text-[0.65rem] px-2 py-0.5 rounded-full border font-medium ${POTENTIAL_COLOR[e.potential] || POTENTIAL_COLOR.dusuk}`}>
                      {e.potential === "yuksek" ? "Yüksek" : e.potential === "orta" ? "Orta" : "Düşük"}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {filtered.length > 100 ? (
          <div className="px-4 py-3 text-center text-xs text-slate-500 border-t border-slate-100">
            İlk 100 sonuç gösteriliyor. Filtreyle daraltabilirsiniz. ({filtered.length} toplam)
          </div>
        ) : null}
      </div>
    </TenantShell>
  );
}
